import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';
import api from '../lib/api';
import './Gallery.css';

const placeholderImages = [
  { id: '1', title: 'Wedding Celebration', image_url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600', event_type: 'Wedding' },
  { id: '2', title: 'Corporate Event', image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600', event_type: 'Corporate' },
  { id: '3', title: 'Birthday Party', image_url: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600', event_type: 'Birthday' },
  { id: '4', title: 'Quinceañera', image_url: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600', event_type: 'Quinceañera' },
  { id: '5', title: 'Gala Night', image_url: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600', event_type: 'Gala' },
  { id: '6', title: 'Holiday Party', image_url: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600', event_type: 'Holiday' },
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      const data = await api.getGallery();
      setItems(data.length > 0 ? data : placeholderImages);
    } catch (error) {
      console.error('Error loading gallery:', error);
      setItems(placeholderImages);
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = ['All', ...new Set(items.map(item => item.event_type))];
  const filteredItems = filter === 'All' ? items : items.filter(item => item.event_type === filter);

  return (
    <div className="gallery-page">
      <section className="gallery-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-subtitle">Our Work</span>
            <h1 className="page-title">Gallery</h1>
            <p className="page-description">
              Browse through our portfolio of unforgettable moments captured at events we've been part of.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {/* Filters */}
          <div className="gallery-filters">
            {eventTypes.map((type) => (
              <button
                key={type}
                className={`filter-btn ${filter === type ? 'active' : ''}`}
                onClick={() => setFilter(type)}
              >
                {type}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner" />
            </div>
          ) : (
            <motion.div className="gallery-grid" layout>
              <AnimatePresence>
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="gallery-item"
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    onClick={() => setSelectedItem(item)}
                  >
                    <img src={item.image_url} alt={item.title} />
                    <div className="gallery-overlay">
                      <ZoomIn size={32} />
                      <h4>{item.title}</h4>
                      <span>{item.event_type}</span>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedItem(null)}
          >
            <button className="lightbox-close" onClick={() => setSelectedItem(null)}>
              <X size={24} />
            </button>
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selectedItem.image_url} alt={selectedItem.title} />
              <div className="lightbox-info">
                <h3>{selectedItem.title}</h3>
                <span>{selectedItem.event_type}</span>
                {selectedItem.description && <p>{selectedItem.description}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
